import { CargoOwnerOption } from "components/bid/form/config";
import Datetime from "components/shared/datetime";
import Field from "components/shared/field";
import Select, { customFilterSelectStyles, defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { bidPaymentStatuses, documentsTrackingStatusesEnum } from "helpers/enums";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { selectList as counterpartiesList } from "sources/books/cargoOwners";

const filterSelectStyles = {
    ...customFilterSelectStyles,
    container: (styles) => ({ ...customFilterSelectStyles.container(styles), width: "150px" }),
    control: (styles) => ({
        ...customFilterSelectStyles.control(styles),
        height: "28px",
        minHeight: "28px",
    }),
    valueContainer: (styles) => ({
        ...styles,
        flexWrap: "nowrap",
        height: "26px",
        lineHeight: "18px",
        paddingBottom: 0,
        paddingTop: 0,
    }),
    input: (styles) => ({ ...styles, height: "17px", margin: 0, paddingBottom: 0, paddingTop: 0 }),
    indicatorsContainer: (styles) => ({
        ...customFilterSelectStyles.indicatorsContainer(styles),
        height: "26px",
    }),
    dropdownIndicator: (styles) => ({ ...styles, padding: "4px" }),
    clearIndicator: (styles) => ({ ...styles, padding: "4px" }),
};

const dateLibOptions = {
    className: "b-filter__input w-auto",
    isClearable: true,
    wrapperClassName: "w-auto",
};

const Filters = (props) => {
    const { mergeParams, params } = props;
    const [data, setData] = useState(() => {
        const {
            id,
            status,
            counterpartyId = [],
            statusFixedAtFrom,
            statusFixedAtTo,
            paymentStatus = [],
        } = params.customFilters;

        return { id, status, counterpartyId, statusFixedAtFrom, statusFixedAtTo, paymentStatus };
    });

    const statusOptions = useMemo(
        () => [
            { label: "Не отправлено", value: "null" },
            { label: "Доставлено", value: "true" },
            { label: "В пути", value: "false" },
        ],
        [],
    );

    const handleChange = ({ name, value }) => setData((prev) => ({ ...prev, [name]: value }));

    const handleUpdate = () => {
        params.customFilters = { isDeleted: false };
        params.preparedFilter.and = [{ isDeleted: false }];
        let parcelFilter = null;

        if (data.id) {
            params.customFilters.id = data.id;
            params.preparedFilter.and.push({ "cast(Id, Model.String)": data.id });
        }

        if (data.counterpartyId?.length) {
            params.customFilters.counterpartyId = data.counterpartyId;
            params.preparedFilter.and.push({ counterpartyId: { in: data.counterpartyId } });
        }

        if (data.paymentStatus?.length) {
            params.customFilters.paymentStatus = data.paymentStatus;
            params.preparedFilter.and.push({ "payment/paymentStatus": { in: data.paymentStatus } });
        }

        const notFinalParcelFilter = { or: [{ "status/isFinal": null }, { "status/isFinal": false }] };
        const noParcelsFilter = { not: { parcels: { any: [] } } };
        const manualStatusFilter = (status) => ({
            and: [noParcelsFilter, { "payment/documentsTrackingStatus": status }],
        });

        if (data.status) {
            params.customFilters.status = data.status;
            if (data.status === "null") {
                params.preparedFilter.and.push({
                    and: [
                        noParcelsFilter,
                        {
                            or: [
                                { "payment/documentsTrackingStatus": null },
                                { "payment/documentsTrackingStatus": documentsTrackingStatusesEnum.NotShipped },
                            ],
                        },
                    ],
                });
            } else if (data.status === "true") {
                params.preparedFilter.and.push({
                    or: [
                        { and: [{ parcels: { any: [] } }, { not: { parcels: { any: notFinalParcelFilter } } }] },
                        manualStatusFilter(documentsTrackingStatusesEnum.Delivered),
                    ],
                });
            } else {
                params.preparedFilter.and.push({
                    or: [
                        { parcels: { any: notFinalParcelFilter } },
                        manualStatusFilter(documentsTrackingStatusesEnum.InTransit),
                    ],
                });
            }
        }

        const statusFixedAt = {};
        if (data.statusFixedAtFrom) {
            params.customFilters.statusFixedAtFrom = data.statusFixedAtFrom;
            statusFixedAt.ge = moment(data.statusFixedAtFrom).startOf("day").toDate();
        }
        if (data.statusFixedAtTo) {
            params.customFilters.statusFixedAtTo = data.statusFixedAtTo;
            statusFixedAt.le = moment(data.statusFixedAtTo).endOf("day").toDate();
        }
        if (Object.keys(statusFixedAt).length > 0) {
            parcelFilter = { ...(parcelFilter || {}), fixedAt: statusFixedAt };
        }
        if (parcelFilter) {
            params.preparedFilter.and.push({ parcels: { any: parcelFilter } });
        }

        mergeParams({ start: 0 });
    };

    const baseSelectConfig = {
        menuPortalTarget: document.body,
        styles: filterSelectStyles,
    };

    const dateConfig = (name, placeholderText) => ({
        inputProps: {
            name,
            type: "date",
        },
        value: data[name],
        libOptions: {
            ...dateLibOptions,
            placeholderText,
        },
        onChange: handleChange,
    });

    const idConfig = {
        label: "ID заявки",
        inputProps: {
            name: "id",
            type: "number",
            step: "1",
            className: "b-filter__input",
            style: { width: "200px" },
            placeholder: "ID заявки",
        },
        value: data.id,
        onChange: handleChange,
    };

    const statusConfig = {
        ...baseSelectConfig,
        inputProps: { name: "document" },
        placeholder: "Статус документа",
        options: statusOptions,
        value: data.status,
        onChange: ({ value }) => handleChange({ name: "status", value }),
    };

    const counterpartyConfig = {
        ...baseSelectConfig,
        ...defaultMultipleSelectProps,
        placeholder: "Контрагент",
        inputProps: { name: "counterpartyId" },
        propName: ({ name, inn, kpp }) => {
            const details = [inn, kpp].filter(Boolean).join("/");
            return details ? `${name} (${details})` : name;
        },
        url: counterpartiesList,
        components: { Option: CargoOwnerOption, ValueContainer },
        filterPropName: ["name", "inn", "kpp"],
        value: data.counterpartyId,
        onChange: handleChange,
        styles: { ...filterSelectStyles, menu: (styles) => ({ ...styles, zIndex: 11, width: "250px" }) },
    };

    const paymentStatusConfig = {
        ...baseSelectConfig,
        ...defaultMultipleSelectProps,
        inputProps: { name: "paymentStatus" },
        placeholder: "Статус оплаты",
        options: bidPaymentStatuses,
        value: data.paymentStatus,
        onChange: handleChange,
        components: { ValueContainer },
    };

    return (
        <div className="d-flex align-items-center flex-wrap mx-3 mb-2 gap-1">
            <Field {...idConfig} />
            <Select {...counterpartyConfig} />
            <Select {...statusConfig} />
            <Datetime {...dateConfig("statusFixedAtFrom", "Дата статуса от")} />
            <Datetime {...dateConfig("statusFixedAtTo", "Дата статуса до")} />
            <Select {...paymentStatusConfig} />
            <div className="btn btn-sm btn-brand" onClick={handleUpdate}>
                Обновить
            </div>
        </div>
    );
};

export default Filters;
